import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowRight, ArrowLeft, Heart, Sparkles, Camera, Scissors } from "lucide-react";
import { CombIcon, LipstickIcon, SparkleIcon, LogoWordmark } from "../components/BrandIcons";
import { useContent } from "../context/ContentContext";
import PortfolioCard from "../components/PortfolioCard";
import Lightbox from "../components/Lightbox";
import ServiceDetailModal from "../components/ServiceDetailModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { Heart, Sparkles, Camera, Scissors };

export default function Home() {
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [detailKey, setDetailKey] = useState(null); // service id whose pop-out is open
  const { content, instagram, servicePricing } = useContent();
  const detailByGroup = (servicePricing || []).reduce((acc, g) => { acc[g.id] = g; return acc; }, {});

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", dragFree: false });
  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(() => {});
    axios.get(`${API}/portfolio`).then((r) => setPortfolio(r.data)).catch(() => {});
    axios.get(`${API}/testimonials`).then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const home = content.home || {};
  const heroImg = home.hero_image || "/images/placeholders/hero.jpg";
  const carouselItems = portfolio.slice(0, 10);

  return (
    <div data-testid="home-page">
      {/* HERO — vertical stack so the wordmark is never obscured */}
      <section className="relative grain-coral overflow-hidden border-b-2 border-ink" data-testid="home-hero">
        <div className="relative z-10 mx-auto max-w-7xl px-5 pt-14 pb-12 md:px-10 md:pt-20 md:pb-16">
          <div className="animate-fadeUp md:text-center">
            <p className="eyebrow text-ink text-xs md:text-sm">Makeup Artist · Hair Stylist · Melbourne</p>

            {/* Client-designed brand poster — used as the hero logo.
                The PNG already carries the pink speckled panel and hand-drawn
                RMoRrison wordmark exactly as designed. Centered on desktop,
                slightly larger for visual presence. */}
            <div className="mt-8 mx-auto w-full max-w-xl md:max-w-3xl" data-testid="hero-logo-wrap">
              <img
                src="/brand/rmorrison-logo.png"
                alt="RMorrison — Makeup Artist & Hair Stylist"
                width={3576}
                height={1456}
                className="block w-full h-auto select-none"
                draggable={false}
                loading="eager"
                fetchpriority="high"
                data-testid="hero-logo"
              />
            </div>

            <h1 className="heading mt-8 text-2xl md:text-4xl text-ink max-w-2xl md:mx-auto">
              {home.hero_headline || "Makeup Artist. Hair Stylist. Main-character energy."}
            </h1>

            <div className="mt-8 flex flex-wrap gap-3 md:justify-center">
              <Link to="/contact" className="btn-brut" data-testid="hero-enquire-btn">
                {home.cta_primary || "Enquire now"} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/portfolio" className="btn-brut ghost" data-testid="hero-view-work-btn">
                {home.cta_secondary || "View the work"}
              </Link>
            </div>
          </div>

          {/* Landscape hero image sitting UNDER the text — no clipping */}
          <div className="mt-10 md:mt-14 grain-pink card-brut relative overflow-hidden aspect-[16/7] md:aspect-[16/6]" data-testid="hero-image-wrap">
            <img
              src={heroImg}
              alt="Bold editorial makeup look"
              className="absolute inset-0 h-full w-full object-cover mix-blend-multiply"
              style={{ objectPosition: "50% 35%" }}
              loading="eager"
            />
            <div className="absolute left-3 top-3 text-ink"><LipstickIcon /></div>
            <div className="absolute left-3 bottom-3 text-ink"><CombIcon /></div>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-ink"><SparkleIcon className="h-8 w-8" /></div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-ink text-cream border-b-2 border-ink overflow-hidden" data-testid="home-marquee">
        <div className="marquee-track py-4">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-10 px-5 shrink-0">
              {(home.marquee_items || ["Weddings", "Editorial", "Film & TV", "Photoshoots", "Formals", "Live Theatre", "Mobile & Studio", "Melbourne + beyond"]).map((w, idx) => (
                <span key={`${k}-${idx}-${w}`} className="eyebrow text-cream text-sm flex items-center gap-10">
                  {w} <SparkleIcon className="h-4 w-4 text-coral" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT INTRO */}
      <section className="border-b-2 border-ink bg-cream" data-testid="home-about-intro">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 md:grid-cols-12 md:px-10 md:py-28">
          <div className="md:col-span-5">
            <p className="eyebrow text-ink/70 text-xs">{home.about_intro_eyebrow || "About"}</p>
            <h2 className="heading mt-3 text-3xl md:text-5xl">{home.about_intro_headline || "Oh hey, you found me. Lucky you..."}</h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-lg text-ink/90 leading-relaxed">
            {(home.about_intro_paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
            <Link to="/about" className="btn-brut" data-testid="about-more-btn">{home.about_intro_cta || "More about me"} <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* SERVICES — simplified to 2 cards */}
      <section className="grain-pink border-b-2 border-ink relative overflow-hidden" data-testid="home-services">
        <div className="relative z-10 mx-auto max-w-7xl px-5 py-20 md:px-10 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <p className="eyebrow text-ink/80 text-xs">{home.services_eyebrow || "Services"}</p>
              <h2 className="heading mt-3 text-3xl md:text-5xl text-ink">{home.services_headline || "What I do, beautifully."}</h2>
            </div>
            <Link to="/services" className="btn-brut self-start" data-testid="services-all-btn">{home.services_cta || "All services"} <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {services.slice(0, 2).map((s) => {
              // Pick a brand SVG icon based on stored icon string.
              const useSparkle = (s.icon || "").toLowerCase().includes("spark") || (s.title || "").toLowerCase().includes("wedding") || (s.title || "").toLowerCase().includes("event");
              const hasDetails = !!detailByGroup[s.id];
              return (
                <article
                  key={s.id}
                  className={`card-brut bg-cream p-8 md:p-10 flex flex-col text-left ${hasDetails ? "cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-coral/40" : ""}`}
                  data-testid={`service-card-${s.id}`}
                  onClick={hasDetails ? () => setDetailKey(s.id) : undefined}
                  onKeyDown={hasDetails ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailKey(s.id); } } : undefined}
                  role={hasDetails ? "button" : undefined}
                  tabIndex={hasDetails ? 0 : undefined}
                  aria-label={hasDetails ? `View ${s.title} pricing and details` : undefined}
                >
                  <div className="h-14 w-14 grid place-items-center border-2 border-ink bg-coral mb-5">
                    {useSparkle ? <SparkleIcon className="h-7 w-7 text-ink" /> : <Scissors className="h-7 w-7 text-ink" />}
                  </div>
                  <h3 className="heading text-2xl md:text-3xl text-ink">{s.title}</h3>
                  {s.tagline && <p className="eyebrow mt-2 text-[0.65rem] text-ink/70">{s.tagline}</p>}
                  <p className="mt-4 text-lg text-ink/90 leading-relaxed">{s.description}</p>
                  {hasDetails && (
                    <span className="mt-6 inline-flex items-center gap-2 eyebrow text-[0.7rem] text-ink underline underline-offset-[6px] decoration-2" data-testid={`service-card-cta-${s.id}`}>
                      View pricing & details <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FACES LATELY — carousel, no overlays */}
      <section className="border-b-2 border-ink bg-cream" data-testid="home-portfolio-preview">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-10 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <p className="eyebrow text-ink/70 text-xs">{home.portfolio_eyebrow || "The Work"}</p>
              <h2 className="heading mt-3 text-3xl md:text-5xl">{home.portfolio_headline || "Faces, lately."}</h2>
            </div>
            <div className="hidden md:flex gap-2">
              <button onClick={scrollPrev} className="btn-brut ghost !p-3" aria-label="Previous image" data-testid="carousel-prev"><ArrowLeft className="h-4 w-4" /></button>
              <button onClick={scrollNext} className="btn-brut ghost !p-3" aria-label="Next image" data-testid="carousel-next"><ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>

          <div className="overflow-hidden" ref={emblaRef} data-testid="portfolio-carousel">
            <div className="flex gap-4 md:gap-6">
              {carouselItems.map((p, idx) => (
                <div key={p.id} className="shrink-0 basis-[78%] sm:basis-[48%] lg:basis-[32%]">
                  <PortfolioCard item={p} idx={idx} onClick={() => setLightboxIdx(idx)} noCaption ratio="aspect-[4/5]" />
                </div>
              ))}
            </div>
          </div>

          {/* Mobile arrows below */}
          <div className="mt-6 flex md:hidden justify-center gap-2">
            <button onClick={scrollPrev} className="btn-brut ghost !p-3" aria-label="Previous image"><ArrowLeft className="h-4 w-4" /></button>
            <button onClick={scrollNext} className="btn-brut ghost !p-3" aria-label="Next image"><ArrowRight className="h-4 w-4" /></button>
          </div>

          <div className="mt-10 flex justify-center">
            <Link to="/portfolio" className="btn-brut" data-testid="see-full-gallery-btn">
              See full gallery <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — slightly smaller cards, 3 across on desktop */}
      <section className="grain-coral border-b-2 border-ink relative overflow-hidden" data-testid="home-testimonials">
        <div className="relative z-10 mx-auto max-w-7xl px-5 py-20 md:px-10 md:py-28">
          <p className="eyebrow text-ink text-xs">{home.testimonials_eyebrow || "Kind words"}</p>
          <h2 className="heading mt-3 text-3xl md:text-5xl text-ink">{home.testimonials_headline || "They said it, not me."}</h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 md:grid-cols-3">
            {testimonials.slice(0, 3).map((t) => (
              <blockquote key={t.id} className="card-brut bg-cream p-5" data-testid={`testimonial-${t.id}`}>
                <SparkleIcon className="h-4 w-4 text-coral" />
                <p className="mt-2 text-sm md:text-base text-ink/90 leading-relaxed">“{t.quote}”</p>
                <footer className="mt-3 eyebrow text-[0.65rem] text-ink">— {t.name} <span className="text-ink/60">/ {t.role}</span></footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* VENDOR TEASER */}
      <section className="bg-cream border-b-2 border-ink" data-testid="home-vendors-teaser">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-10 md:py-28">
          <div className="grid items-center gap-10 md:grid-cols-12">
            <div className="md:col-span-7">
              <p className="eyebrow text-ink/70 text-xs">{home.vendors_eyebrow || "The little black book"}</p>
              <h2 className="heading mt-3 text-3xl md:text-5xl">{home.vendors_headline || "Vendors I love."}</h2>
              <p className="mt-5 max-w-xl text-lg text-ink/90">{home.vendors_paragraph || "Photographers, celebrants, florists, venues, stylists and creatives I’d send my best friend to. Hand-picked, no kickbacks, just taste."}</p>
              <Link to="/vendors" className="btn-brut mt-6" data-testid="vendors-cta-btn">{home.vendors_cta || "Browse the list"} <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="md:col-span-5 grain-pink card-brut p-8 relative overflow-hidden">
              <div className="flex flex-wrap gap-3">
                {["Photographers", "Celebrants", "Florists", "Venues", "Stylists", "Entertainment", "Other Creatives"].map((c) => (
                  <span key={c} className="eyebrow text-[0.65rem] border-2 border-ink bg-cream px-3 py-2">{c}</span>
                ))}
              </div>
              <div className="absolute right-3 top-3 text-ink"><SparkleIcon /></div>
            </div>
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      {instagram.length > 0 && (
        <section className="bg-cream border-b-2 border-ink" data-testid="home-instagram">
          <div className="mx-auto max-w-7xl px-5 py-20 md:px-10 md:py-24">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
              <div>
                <p className="eyebrow text-ink/70 text-xs">{home.instagram_eyebrow || "Lately on the gram"}</p>
                <h2 className="heading mt-3 text-3xl md:text-5xl">{home.instagram_headline || "@the.rachel.morrison"}</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {instagram.slice(0, 6).map((g) => (
                <a key={g.id} href={g.link || "#"} target="_blank" rel="noreferrer" className="card-brut block aspect-square overflow-hidden bg-pinkpanel" data-testid={`ig-${g.id}`}>
                  <img src={g.image} alt={g.caption || "Instagram"} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="grain-pink relative overflow-hidden" data-testid="home-final-cta">
        <div className="relative z-10 mx-auto max-w-5xl px-5 py-20 md:px-10 md:py-28 text-center">
          <p className="eyebrow text-ink text-xs">{home.final_cta_eyebrow || "Ready when you are"}</p>
          <h2 className="heading mt-3 text-4xl md:text-6xl text-ink">{home.final_cta_headline || "Let’s make a moment."}</h2>
          <p className="mt-5 text-lg text-ink/90 max-w-xl mx-auto">{home.final_cta_paragraph || "Tell me about your event, your vibe, your wildest reference image. I’ll bring the brushes and the calm."}</p>
          <Link to="/contact" className="btn-brut mt-8" data-testid="final-enquire-btn">{home.final_cta_button || "Send an enquiry"} <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
      {lightboxIdx >= 0 && (
        <Lightbox items={carouselItems} index={lightboxIdx} onChange={setLightboxIdx} onClose={() => setLightboxIdx(-1)} />
      )}
      <ServiceDetailModal
        open={!!detailKey}
        detail={detailKey ? detailByGroup[detailKey] : null}
        onClose={() => setDetailKey(null)}
      />
    </div>
  );
}
